import React, { useId, useState } from "react";
import {
  ArrowLeft,
  CheckCircle2,
  ChevronRight,
  FileUp,
  Replace,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { KycStep } from "../components/KycStep";
import { StepFooter } from "../components/StepFooter";
import { StepHeader } from "../components/StepHeader";

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const VALID_FILE_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
];

export function AddressProofStep({
  addressProof,
  error,
  busy,
  onChange,
  onBack,
  onContinue,
}) {
  const { t } = useTranslation();
  const inputId = useId();
  const [localError, setLocalError] = useState("");

  const handleFile = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (!VALID_FILE_TYPES.includes(file.type) || file.size > MAX_FILE_SIZE) {
      setLocalError(t("kyc.validation.addressProofFile"));
      return;
    }

    if (addressProof.previewUrl?.startsWith("blob:")) {
      URL.revokeObjectURL(addressProof.previewUrl);
    }

    setLocalError("");
    onChange({
      file,
      previewUrl: URL.createObjectURL(file),
      uploadedUrl: "",
      fileName: file.name,
      fileType: file.type,
    });
  };

  const handleContinue = () => {
    if (!addressProof.previewUrl) {
      setLocalError(t("kyc.validation.addressProof"));
      return;
    }
    onContinue();
  };

  const isPdf =
    addressProof.fileType === "application/pdf" ||
    /\.pdf(?:$|[?#])/i.test(addressProof.previewUrl || "");

  return (
    <KycStep
      labelledBy="kyc-address-proof-title"
      className="address-proof-step"
    >
      <StepHeader
        titleId="kyc-address-proof-title"
        centered
        compact
        title={t("kyc.addressProof.title")}
        description={t("kyc.addressProof.description")}
      />

      {addressProof.previewUrl ? (
        <div className="address-proof-card mx-auto mt-5 max-w-xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_16px_40px_-28px_rgba(15,23,42,0.45)]">
          <div className="address-proof-preview flex h-[min(21dvh,10rem)] min-h-0 items-center justify-center bg-slate-100">
            {isPdf ? (
              <iframe
                src={`${addressProof.previewUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                title={t("kyc.addressProof.previewTitle")}
                className="h-full w-full border-0"
                scrolling="no"
              />
            ) : (
              <img
                src={addressProof.previewUrl}
                alt={t("kyc.addressProof.previewAlt")}
                className="size-full object-contain"
              />
            )}
          </div>
          <div className="flex items-center gap-3 border-t border-slate-200 bg-white px-3.5 py-2.5">
            <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-700">
              <CheckCircle2 className="size-4" aria-hidden="true" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-slate-900">
                {addressProof.fileName || t("kyc.addressProof.provided")}
              </p>
              <p className="text-xs text-slate-500">
                {t("kyc.addressProof.ready")}
              </p>
            </div>
            <label
              htmlFor={inputId}
              className="inline-flex min-h-9 shrink-0 cursor-pointer items-center justify-center gap-1.5 rounded-lg px-2.5 text-xs font-semibold text-teal-700 transition hover:bg-teal-50 hover:text-teal-800"
            >
              <Replace className="size-3.5" aria-hidden="true" />
              {t("kyc.addressProof.changeFile")}
            </label>
          </div>
        </div>
      ) : (
        <label
          htmlFor={inputId}
          className="address-proof-picker group mx-auto mt-6 flex min-h-[6.75rem] w-full max-w-xl cursor-pointer items-center gap-4 rounded-2xl border border-slate-200 bg-white px-4 py-4 text-left shadow-[0_14px_35px_-28px_rgba(15,23,42,0.55)] transition duration-200 hover:-translate-y-px hover:border-teal-200 hover:shadow-[0_18px_40px_-26px_rgba(15,118,110,0.35)]"
        >
          <span className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-teal-50 text-teal-700 ring-1 ring-teal-100 transition group-hover:bg-teal-100/70">
            <FileUp className="size-5" aria-hidden="true" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-[15px] font-semibold text-slate-950">
              {t("kyc.addressProof.chooseFile")}
            </span>
            <span className="mt-1 block text-xs leading-5 text-slate-500">
              {t("kyc.addressProof.requirements")}
            </span>
          </span>
          <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-slate-50 text-slate-400 transition group-hover:bg-teal-50 group-hover:text-teal-700">
            <ChevronRight className="size-4" aria-hidden="true" />
          </span>
        </label>
      )}

      <input
        id={inputId}
        type="file"
        accept="application/pdf,image/jpeg,image/png,image/webp"
        className="sr-only"
        onChange={handleFile}
      />

      {(localError || error) && (
        <p className="mt-3 text-center text-xs text-red-700" role="alert">
          {localError || error}
        </p>
      )}

      {addressProof.previewUrl ? (
        <div className="mx-auto max-w-xl">
          <StepFooter
            onBack={onBack}
            onContinue={handleContinue}
            continueLabel={
              busy
                ? t("kyc.addressProof.uploading")
                : t("kyc.addressProof.continue")
            }
            busy={busy}
            compact
          />
        </div>
      ) : (
        <button
          type="button"
          onClick={onBack}
          className="mx-auto mt-4 inline-flex min-h-10 items-center justify-center gap-2 rounded-lg px-4 text-sm font-semibold text-slate-500 transition hover:bg-slate-50 hover:text-slate-800"
        >
          <ArrowLeft className="size-4" aria-hidden="true" />
          {t("common.back")}
        </button>
      )}
    </KycStep>
  );
}
